import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarDays, Copy, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Calendar subscription card — shows the user's stable webcal:// feed URL, a
 * copy button, and step-by-step instructions for Apple Calendar (iPhone) and
 * Google Calendar.  Once subscribed, iOS pushes native notifications for
 * upcoming shifts automatically.
 */
export default function CalendarSubscribe() {
  const [icsUrl, setIcsUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);

  const buildFullUrl = (path) => {
    if (!path) return "";
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    if (/^https?:\/\//i.test(path)) return path;
    return `${base}${path}`;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/calendar-token");
      setIcsUrl(buildFullUrl(data.ics_path || data.ics_url || ""));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't get feed URL");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const webcalUrl = (icsUrl || "").replace(/^https?:/i, "webcal:");
  const gcalUrl   = icsUrl ? `https://calendar.google.com/calendar/u/0/r/settings/addbyurl?url=${encodeURIComponent(icsUrl)}` : "";

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Couldn't copy — long-press to select the URL"); }
  };

  const rotate = async () => {
    if (!window.confirm("This will invalidate the old link. Any devices already subscribed will need to re-subscribe. Continue?")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/me/calendar-token/rotate");
      setIcsUrl(buildFullUrl(data.ics_path || data.ics_url || ""));
      toast.success("New link generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to rotate");
    }
    setRotating(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="calendar-subscribe">
      <div className="flex items-center gap-2 font-display font-semibold">
        <CalendarDays size={16} className="text-blue-600" /> Add shifts to your phone calendar
      </div>
      <div className="text-sm text-slate-500 mt-0.5">
        Subscribe once and every future shift shows up in Apple Calendar (iPhone) or Google Calendar — with a 30-minute reminder before each shift starts.
      </div>

      <div className="mt-4 grid gap-2">
        <div className="flex gap-2">
          <Input readOnly value={loading ? "Loading…" : webcalUrl} data-testid="cal-url" className="font-mono text-xs" />
          <Button variant="outline" onClick={() => copy(webcalUrl)} data-testid="cal-copy">
            <Copy size={14} className="mr-1" /> Copy
          </Button>
        </div>
        <div className="text-[11px] text-slate-500">
          This is your private subscribe URL — keep it secret. If it ever leaks you can rotate it below.
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <a href={webcalUrl} data-testid="cal-open-apple" className="rounded-xl border border-slate-200 p-3 hover:bg-slate-50 flex items-start gap-2">
          <ExternalLink size={14} className="mt-0.5 text-blue-600" />
          <div>
            <div className="text-sm font-semibold">Apple Calendar (iPhone)</div>
            <div className="text-[11px] text-slate-500">Tap this on your iPhone — iOS will offer to "Subscribe". Alerts appear automatically.</div>
          </div>
        </a>
        <a href={gcalUrl} target="_blank" rel="noreferrer" data-testid="cal-open-google" className="rounded-xl border border-slate-200 p-3 hover:bg-slate-50 flex items-start gap-2">
          <ExternalLink size={14} className="mt-0.5 text-blue-600" />
          <div>
            <div className="text-sm font-semibold">Google Calendar</div>
            <div className="text-[11px] text-slate-500">Opens the "Add by URL" screen in your Google account.</div>
          </div>
        </a>
      </div>

      <div className="mt-4">
        <button onClick={rotate} disabled={rotating} className="text-[11px] text-slate-500 hover:text-red-700 inline-flex items-center gap-1" data-testid="cal-rotate">
          <RefreshCw size={11} /> {rotating ? "Rotating…" : "Rotate my link"}
        </button>
      </div>
    </div>
  );
}
